import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from './middleware/apiMiddleware';

/**
 * USER SLICE - Authentication State Management
 * Pattern: Follows middleware-based workflow with centralized API calls
 *
 * Each operation (signup, login, forgot-password) has separate loading/error states
 * and uses middleware for centralized API handling via apiCallBegan action
 */

const initialState = {
    // User data, initialize with token from localStorage if available to persist auth state across reloads
    user: localStorage.getItem('authToken') ? { token: localStorage.getItem('authToken') } : null,

    // Login operation states
    loginLoading: false,
    loginError: null,

    // Forgot Password operation states
    forgotPasswordLoading: false,
    forgotPasswordError: null,
    forgotPasswordSuccess: false,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        // ============ SIGNUP REDUCERS REMOVED ============

        // ============ LOGIN REDUCERS ============
        beginLoginLoader: (state) => {
            state.loginLoading = true;
            state.loginError = null;
        },
        setLoginData: (state, action) => {
            state.loginLoading = false;
            state.user = action.payload;
            state.loginError = null;
            if (action.payload?.token) {
                localStorage.setItem('authToken', action.payload.token);
            }
        },
        loginError: (state, action) => {
            state.loginLoading = false;
            state.loginError = action.payload?.errorMessage || 'Login failed';
        },

        // ============ FORGOT PASSWORD REDUCERS ============
        beginForgotPasswordLoader: (state) => {
            state.forgotPasswordLoading = true;
            state.forgotPasswordError = null;
        },
        setForgotPasswordData: (state) => {
            state.forgotPasswordLoading = false;
            state.forgotPasswordError = null;
            state.forgotPasswordSuccess = true;
        },
        forgotPasswordError: (state, action) => {
            state.forgotPasswordLoading = false;
            state.forgotPasswordError = action.payload?.errorMessage || 'Forgot password request failed';
        },
        resetForgotPasswordSuccess: (state) => {
            state.forgotPasswordSuccess = false;
        },

        // ============ GENERAL REDUCERS ============
        clearUserData: (state) => {
            state.user = null;
            localStorage.removeItem('authToken');
        }
    },
});

// ============ EXPORT ACTIONS ============
export const {
    beginSignupLoader,
    setSignupData,
    signupError,
    beginLoginLoader,
    setLoginData,
    loginError,
    beginForgotPasswordLoader,
    setForgotPasswordData,
    forgotPasswordError,
    resetForgotPasswordSuccess,
    clearUserData
} = userSlice.actions;

// ============ THUNK ACTIONS - Dispatch to middleware ============

/**
 * SIGNUP USER ACTION - REMOVED
 */
// export const signupUser = (userData) => (dispatch) => { ... };

/**
 * LOGIN USER ACTION
 * Dispatches middleware action to handle login API call
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 *
 * @param {Object} credentials - { email, password }
 */
export const loginUser = (credentials) => (dispatch) => {
    const url = '/login';
    const method = 'POST';

    return dispatch(
        apiCallBegan({
            url,
            method,
            data: {
                type: credentials.type,
                value: credentials.value,
                password: credentials.password,
                captchaToken: credentials.captchaToken,
            },
            onStart: beginLoginLoader.type,
            onSuccess: setLoginData.type,
            onError: loginError.type,
            showGlobalError: true,
        })
    );
};

/**
 * FORGOT PASSWORD USER ACTION
 * Dispatches middleware action to handle forgot password API call
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 *
 * @param {Object} payload - { email }
 */
export const forgotPasswordUser = (payload) => (dispatch) => {
    const url = '/forgot-password';
    const method = 'PUT';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: payload,
            onStart: beginForgotPasswordLoader.type,
            onSuccess: setForgotPasswordData.type,
            onError: forgotPasswordError.type,
            showGlobalError: true,
        })
    );
};

// ============ SELECTORS - For component state access ============

/**
 * User Data Selectors
 */
export const selectUser = (state) => state.user.user;
export const selectUserId = (state) => state.user.user?.id;
export const selectUserEmail = (state) => state.user.user?.email;
export const selectUserName = (state) => state.user.user?.name;
export const selectIsUserLoggedIn = (state) => state.user.user !== null;

/**
 * Signup State Selectors
 */
// export const selectSignupLoading = (state) => state.user.signupLoading;
// export const selectSignupError = (state) => state.user.signupError;

/**
 * Login State Selectors
 */
export const selectLoginLoading = (state) => state.user.loginLoading;
export const selectLoginError = (state) => state.user.loginError;

/**
 * Forgot Password State Selectors
 */
export const selectForgotPasswordLoading = (state) => state.user.forgotPasswordLoading;
export const selectForgotPasswordError = (state) => state.user.forgotPasswordError;
export const selectForgotPasswordSuccess = (state) => state.user.forgotPasswordSuccess;

/**
 * Combined Selectors
 */
export const selectIsAnyOperationLoading = (state) =>
    state.user.signupLoading || state.user.loginLoading || state.user.forgotPasswordLoading;

export default userSlice.reducer;
